import { FrameworkClasses } from '../framework/frameworkClasses';
import { FrameworkClassPartType } from '../framework/framework-enums';
import { FrameworkClassType } from '../framework/framework-types';
import { collectLastTopLevelVarAssignmentsByName, splitCodeLines } from './pythonSourceScanUtils';

let frameworkClassNames: ReadonlySet<string> | null = null;
let frameworkMechanismClassNames: ReadonlySet<string> | null = null;
let frameworkSensorClassNames: ReadonlySet<string> | null = null;
let frameworkElectronicClassNames: ReadonlySet<string> | null = null;

/** Framework constructor class names for all registered device/part types. */
export function getFrameworkClassNames(): ReadonlySet<string> {
  if (!frameworkClassNames) {
    frameworkClassNames = new Set(FrameworkClasses.map((frameworkClass) => frameworkClass.name));
  }
  return frameworkClassNames;
}

/** Framework constructor class names for sensor part types. */
export function getFrameworkSensorClassNames(): ReadonlySet<string> {
  if (!frameworkSensorClassNames) {
    frameworkSensorClassNames = new Set(
      FrameworkClasses.filter((frameworkClass) => frameworkClass.partType === FrameworkClassPartType.Sensors).map(
        (frameworkClass) => frameworkClass.name
      )
    );
  }
  return frameworkSensorClassNames;
}

/** Framework constructor class names for Electronic devices (sensors, motors, BitAdapter, etc.). */
export function getFrameworkElectronicClassNames(): ReadonlySet<string> {
  if (!frameworkElectronicClassNames) {
    frameworkElectronicClassNames = new Set(
      FrameworkClasses.filter((frameworkClass) => frameworkClass.type === FrameworkClassType.Electronic).map((frameworkClass) => frameworkClass.name)
    );
  }
  return frameworkElectronicClassNames;
}

/**
 * Splits framework action-arg type strings into class-name candidates.
 * Supports single names and PEP 604 unions (`A | B | C`).
 */
export function parseFrameworkClassArgTypeNames(type: string | undefined | null): string[] {
  const rawType = String(type || '').trim();
  if (!rawType) return [];
  return rawType
    .split('|')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

/**
 * True when `type` is a framework Electronic class name (or a union of only those)
 * used as an action argument (e.g. `MagneticProximitySwitch`,
 * `BeamBreakSensor | DistanceSensor | MagneticProximitySwitch`).
 */
export function isFrameworkElectronicClassArgType(type: string | undefined | null): boolean {
  const classNames = parseFrameworkClassArgTypeNames(type);
  if (classNames.length === 0) return false;
  const electronicClassNames = getFrameworkElectronicClassNames();
  return classNames.every((className) => electronicClassNames.has(className));
}

export type CollectElectronicVariableNamesForClassOpts = {
  /** Ignores declarations at or after this 1-based source line number. */
  availableBeforeLineNumber?: number;
};

/**
 * Declared top-level variable names in `programCode` constructed as `className(...)`.
 * `className` may be a single Electronic class or a `|`-union of Electronic classes.
 * Used to populate action-arg electronics dropdowns.
 */
export function collectElectronicVariableNamesForClass(
  programCode: string,
  className: string,
  opts?: CollectElectronicVariableNamesForClassOpts
): string[] {
  const allowedClassNames = new Set(parseFrameworkClassArgTypeNames(className));
  if (allowedClassNames.size === 0 || !String(programCode || '').trim()) return [];
  const beforeLineIndex =
    typeof opts?.availableBeforeLineNumber === 'number' && Number.isFinite(opts.availableBeforeLineNumber)
      ? Math.max(0, Math.trunc(opts.availableBeforeLineNumber) - 1)
      : undefined;
  return collectLastTopLevelVarAssignmentsByName(splitCodeLines(programCode), {
    allowedClassNames,
    beforeLineIndex,
  }).map((assignment) => assignment.variableName);
}

/** Resolves a configured Electronic variable to its last effective top-level constructor class. */
export function resolveElectronicVariableClassName(
  programCode: string,
  variableName: string,
  className: string,
  opts?: CollectElectronicVariableNamesForClassOpts
): string | null {
  const normalizedVariableName = String(variableName || '').trim();
  const allowedClassNames = new Set(parseFrameworkClassArgTypeNames(className));
  if (!normalizedVariableName || allowedClassNames.size === 0 || !String(programCode || '').trim()) return null;
  const beforeLineIndex =
    typeof opts?.availableBeforeLineNumber === 'number' && Number.isFinite(opts.availableBeforeLineNumber)
      ? Math.max(0, Math.trunc(opts.availableBeforeLineNumber) - 1)
      : undefined;
  return (
    collectLastTopLevelVarAssignmentsByName(splitCodeLines(programCode), {
      allowedClassNames,
      beforeLineIndex,
    }).find((assignment) => assignment.variableName === normalizedVariableName)?.className ?? null
  );
}

/** Allowed constructor classes when anchoring on a framework action target variable. */
export function getFrameworkActionTargetAllowedClassNames(frameworkClassName?: string | null): ReadonlySet<string> | undefined {
  return frameworkClassName ? new Set([frameworkClassName]) : undefined;
}

/** Mechanism classes shown in mechanism hardware UI (excludes DriveBase). */
export function getFrameworkMechanismClassNames(): ReadonlySet<string> {
  if (!frameworkMechanismClassNames) {
    frameworkMechanismClassNames = new Set(
      FrameworkClasses.filter((frameworkClass) => frameworkClass.type === FrameworkClassType.Mechanism && frameworkClass.uiPanel !== 'DriveBase').map(
        (frameworkClass) => frameworkClass.name
      )
    );
  }
  return frameworkMechanismClassNames;
}
